import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../../styles/colors';
import { FaMedal, FaHandshake, FaLightbulb } from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const AboutSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #fff 0%, #f9f9f9 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${colors.primary}40, transparent);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: ${colors.primary};
  text-align: center;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1s ease-out;
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: ${colors.accent};
    margin: 20px auto 0;
  }
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const Description = styled.div`
  max-width: 800px;
  margin: 0 auto 50px;
  animation: ${fadeIn} 1s ease-out 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${colors.text};
  margin-bottom: 20px;
  text-align: left;
`;

const ValuesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin: 40px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: ${colors.white};
  padding: 40px 30px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  animation: ${slideIn} 0.8s ease-out ${props => props.delay}s;
  opacity: 0;
  animation-fill-mode: forwards;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${colors.accent};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover:before {
    transform: scaleX(1);
  }
`;

const ValueIcon = styled.div`
  font-size: 2.5rem;
  color: ${colors.primary};
  margin-bottom: 20px;
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
`;

const ValueTitle = styled.h3`
  color: ${colors.primary};
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const ValueDescription = styled.p`
  color: ${colors.text};
  font-size: 1rem;
  line-height: 1.6;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 15px 30px;
  background: ${colors.primary};
  color: ${colors.white};
  text-decoration: none;
  border-radius: 5px;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-top: 40px;
  animation: ${fadeIn} 1s ease-out 1.6s;
  opacity: 0;
  animation-fill-mode: forwards;
  
  &:hover {
    background: ${colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

const AboutCompany = () => {
  return (
    <AboutSection>
      <Container>
        <Title>About Rudani Realty and Shree Buildcon Group</Title>
        <AboutContent>
          <Description>
            <Paragraph>
              Rudani Realty and Shree Buildcon Group are renowned names in Mumbai's real estate market, 
              known for their commitment to quality, integrity, and customer satisfaction. Together, they 
              bring decades of experience in delivering exceptional residential projects.
            </Paragraph>
            <Paragraph>
              With a strong focus on innovation and excellence, both developers have established themselves 
              as reliable builders committed to creating homes that exceed expectations. Their collaborative 
              approach and attention to detail have made them respected names in Mumbai's real estate landscape.
            </Paragraph>
          </Description>
          
          <ValuesContainer>
            <ValueCard delay={0.8}>
              <ValueIcon>
                <FaMedal />
              </ValueIcon>
              <ValueTitle>Quality</ValueTitle>
              <ValueDescription>
                Committed to delivering excellence in every project with uncompromising standards
              </ValueDescription>
            </ValueCard>
            
            <ValueCard delay={1.0}>
              <ValueIcon>
                <FaHandshake />
              </ValueIcon>
              <ValueTitle>Trust</ValueTitle>
              <ValueDescription>
                Building relationships through transparency and integrity in all our dealings
              </ValueDescription>
            </ValueCard>
            
            <ValueCard delay={1.2}>
              <ValueIcon>
                <FaLightbulb />
              </ValueIcon>
              <ValueTitle>Innovation</ValueTitle>
              <ValueDescription>
                Embracing modern solutions and technologies for better living experiences
              </ValueDescription>
            </ValueCard>
          </ValuesContainer>
          
          <CTAButton href="/about-us">
            Learn More About Us
          </CTAButton>
        </AboutContent>
      </Container>
    </AboutSection>
  );
};

export default AboutCompany; 